import { useState } from 'react';
import { useBrainStore } from '@/state/brainStore';
import { useSessionStore } from '@/state/sessionStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useActiveStudio } from '@/state/useActiveStudio';
import { activeStudioIds, isAllStudios, studioOptions } from '@/domain/studioScope';
import type { StudioId } from '@/domain/types';
import PayoutCashPanel from '@/screens/payouts/PayoutCashPanel';
import DayClosePanel from '@/screens/payouts/DayClosePanel';

/**
 * Payouts & cash (Prompt E2) — the money surface, split out from the floor
 * (which is for running clients). Per studio: each artist's envelope, on-screen
 * sign-off, the cash ledger (sealed float, working cash, deferral vs float-dip)
 * and the day's close.
 *
 * Single-purpose "today" surface: the cash panel and the close ritual, rendered
 * directly — no History tab, so the day screen stays uncrowded (client, 30 Jul).
 * (The old standalone payout History register was left route-dead by this split
 * and has been removed — tv-6qv.10.)
 */

/** Where the "hide totals" preference lives between visits. */
const HIDE_TOTALS_KEY = 'payouts:hideTotals';

/** Read the remembered preference; defaults to shown when storage is unavailable. */
function readHideTotals(): boolean {
  try {
    return localStorage.getItem(HIDE_TOTALS_KEY) === '1';
  } catch {
    return false;
  }
}

export default function PayoutsCash() {
  const studios = useBrainStore((s) => s.studios);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const { activeStudio, assignedStudios } = useActiveStudio();
  const setActiveStudio = useSessionStore((s) => s.setActiveStudio);

  const activeStudios = Object.values(studios).filter((s) => !s.archived);
  // The switcher select must only offer the persona's own studios: single-studio
  // personas have the top-bar switcher hidden, so an unrestricted list here would
  // let them scope the whole app to a studio they aren't assigned to.
  // branding-3 (L6 review #3): archived studios drop out; the current pick stays.
  const studioChoices = studioOptions(studios, assignedStudios, isAllStudios(activeStudio) ? null : activeStudio);
  // Per-studio surface: 'all' does not aggregate here, so fall back to the
  // persona's first OPEN studio when the switcher is network-wide.
  const studioId = (isAllStudios(activeStudio)
    ? (activeStudioIds(studios, assignedStudios)[0] ?? assignedStudios[0] ?? 'coburg')
    : activeStudio) as StudioId;
  const studio = studios[studioId] ?? activeStudios[0];

  // Collapse the aggregate money bar (the six tiles + workings). Remembered
  // between visits — a manager who reads the day off the rows keeps it folded.
  const [hideTotals, setHideTotals] = useState<boolean>(readHideTotals);
  const toggleTotals = () => {
    setHideTotals((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(HIDE_TOTALS_KEY, next ? '1' : '0');
      } catch {
        // Storage unavailable (private mode) — the toggle still works this
        // session, it just won't be remembered next time.
      }
      return next;
    });
  };

  return (
    <section className="px-8 py-8" data-testid="payouts-cash">
      <header className="flex items-end justify-between max-w-7xl flex-wrap gap-4">
        <div>
          <p className="label-small-caps text-gold-bright">Money</p>
          <h1 className="heading-display text-3xl text-cream mt-2">Payouts &amp; cash</h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={toggleTotals}
            aria-pressed={hideTotals}
            data-testid="payout-hide-totals"
            className="btn-quiet text-[11px] px-2.5 py-1"
          >
            {hideTotals ? 'Show totals' : 'Hide totals'}
          </button>
          <label className="font-inter text-[10px] uppercase tracking-widest text-cream-soft">
            Studio
          </label>
          <select
            value={studio?.id ?? ''}
            onChange={(e) => setActiveStudio(e.target.value as StudioId)}
            className="bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-1.5 text-sm text-cream"
            data-testid="payouts-studio-filter"
          >
            {studioChoices.map((s) => (
              <option key={s.id} value={s.id}>
                {s.brand} · {s.name}
              </option>
            ))}
          </select>
        </div>
      </header>

      <div className="mt-6 max-w-7xl">
        {!studio ? (
          <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
            No active studios.
          </p>
        ) : (
          <>
            <PayoutCashPanel
              studioId={studio.id}
              studioName={studio.name}
              dayIso={clockIso}
              hideTotals={hideTotals}
            />
            <DayClosePanel studioId={studio.id} studioName={studio.name} dayIso={clockIso} />
          </>
        )}
      </div>
    </section>
  );
}
